/**
 * RUN IT dice audio + haptics — synthesized via Web Audio API.
 * No external files; works offline, respects iOS silent switch.
 *
 *   playClack()       — short wood-ish dice impact on reveal
 *   playWinChime()    — ascending triad on win
 *   playLossThud()    — descending minor drop on loss
 *   triggerHaptic()   — Capacitor Haptics (native) or navigator.vibrate (web)
 *   isSoundEnabled()  — reads from localStorage; default true
 *   setSoundEnabled(v)
 */

const STORAGE_KEY = "blk:runit:sound";

export const isSoundEnabled = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === null ? true : v === "1";
  } catch {
    return true;
  }
};

export const setSoundEnabled = (enabled) => {
  try { localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0"); } catch { /* noop */ }
};

let _ctx = null;
const getCtx = () => {
  if (!isSoundEnabled()) return null;
  if (typeof window === "undefined") return null;
  if (!_ctx) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      _ctx = new AC();
    } catch { return null; }
  }
  // Resume on user gesture — browsers suspend by default
  if (_ctx.state === "suspended") { try { _ctx.resume(); } catch { /* noop */ } }
  return _ctx;
};

// ─── Clack ──────────────────────────────────────────────────────────────────
// Chunky wood+bone impact: a short noise burst through a tight bandpass gives
// the "wood" character, stacked with a fast sub-bass thump + a hard transient
// pop. Tuned to feel heavy — like a real casino die slamming felt.
export const playClack = (variant = 0) => {
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  const duration = 0.18;

  // 1) Tight noise burst — the wooden "tok" body
  const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
  const nd = noiseBuf.getChannelData(0);
  for (let i = 0; i < nd.length; i++) nd[i] = (Math.random() * 2 - 1) * (1 - i / nd.length);
  const nSrc = ctx.createBufferSource();
  nSrc.buffer = noiseBuf;
  const nBp = ctx.createBiquadFilter();
  nBp.type = "bandpass";
  nBp.frequency.value = 1400 + (variant * 220);
  nBp.Q.value = 4.5;
  const nLs = ctx.createBiquadFilter();
  nLs.type = "lowshelf";
  nLs.frequency.value = 220;
  nLs.gain.value = 12;
  const nGain = ctx.createGain();
  nGain.gain.setValueAtTime(0.95, now);
  nGain.gain.exponentialRampToValueAtTime(0.001, now + duration);
  nSrc.connect(nBp).connect(nLs).connect(nGain).connect(ctx.destination);
  nSrc.start(now);
  nSrc.stop(now + duration);

  // 2) Sub-bass thump — fast pitch-drop sine for the chest-punch
  const sub = ctx.createOscillator();
  const sGain = ctx.createGain();
  sub.type = "sine";
  sub.frequency.setValueAtTime(95, now);
  sub.frequency.exponentialRampToValueAtTime(38, now + 0.1);
  sGain.gain.setValueAtTime(0.0001, now);
  sGain.gain.exponentialRampToValueAtTime(0.55, now + 0.008);
  sGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
  sub.connect(sGain).connect(ctx.destination);
  sub.start(now);
  sub.stop(now + 0.2);

  // 3) Hard transient click — 1ms square pop adds the "snap"
  const clk = ctx.createOscillator();
  const clkGain = ctx.createGain();
  clk.type = "square";
  clk.frequency.value = 2400 + (variant * 240);
  clkGain.gain.setValueAtTime(0.0001, now);
  clkGain.gain.exponentialRampToValueAtTime(0.35, now + 0.002);
  clkGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.035);
  clk.connect(clkGain).connect(ctx.destination);
  clk.start(now);
  clk.stop(now + 0.05);
};

// ─── Win chime ──────────────────────────────────────────────────────────────
export const playWinChime = () => {
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
  notes.forEach((f, i) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = f;
    const t0 = now + i * 0.09;
    const t1 = t0 + 0.25;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.22, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t1);
    osc.connect(g).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t1 + 0.02);
  });
};

// ─── Loss thud ──────────────────────────────────────────────────────────────
export const playLossThud = () => {
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(220, now);
  osc.frequency.exponentialRampToValueAtTime(60, now + 0.55);
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(0.3, now + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
  osc.connect(g).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.6);
};

// ─── Arena ambience — DISABLED ──────────────────────────────────────────────
// User feedback: the pink-noise "crowd hum" sounded like wind. Stripped out.
// `startArena` / `stopArena` kept as no-ops for backward compatibility.
export const startArena = () => { /* disabled — no ambient bed */ };
export const stopArena = () => { /* disabled */ };

// ─── Haptics ────────────────────────────────────────────────────────────────
export const triggerHaptic = async (style = "light") => {
  if (!isSoundEnabled()) return; // reuse sound toggle to silence everything
  try {
    // Lazy-require so non-Capacitor builds (pure web preview) still work.
    const mod = await import("@capacitor/haptics").catch(() => null);
    if (mod && mod.Haptics) {
      const ImpactStyle = mod.ImpactStyle || {};
      const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
      await mod.Haptics.impact({ style: map[style] || ImpactStyle.Light });
      return;
    }
  } catch { /* fall through */ }
  try {
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      const pattern = { light: 15, medium: 30, heavy: 60 }[style] || 15;
      navigator.vibrate(pattern);
    }
  } catch { /* noop */ }
};
